'use client';

/**
 * TimeRangePicker — Phase F2 Sprint 1.
 *
 * Standard dashboard time-range selector used at the top of every
 * analytics surface. Closes the universal gap from the Phase C audit:
 * 0/6 dashboards had a user-selectable time range.
 *
 * Two interaction modes (combined in the same widget):
 *   1. Preset chips — Today / 7d / 30d / 90d / 12m / Custom
 *   2. Custom mode opens two date inputs (from + to)
 *
 * Emits a normalised `TimeRange` value: `{ preset, from?, to? }`. Consumers
 * resolve `preset → ISO date range` with the required tenant timezone, using
 * the shared timezone helpers to turn local calendar days into UTC bounds.
 *
 * Consumer flow:
 *   const [range, setRange] = useState<TimeRange>({ preset: '30d' });
 *   <TimeRangePicker timezone={tenantTimezone} value={range} onChange={setRange} />
 *   // Server-side: resolve range.preset (or from/to) to the date filter.
 */

import React from 'react';
import { dateOnlyInTz, dayBoundsInTz } from '@statura/utils/timezone';
import { addDaysIso, addMonthsIso, parseIsoDateInput } from '../date-entry-helpers';
import { Calendar } from 'lucide-react';
import { cn } from '../../lib/utils';
import { DateEntryInput } from '../date-entry-input';
import { SegmentedControl } from '../segmented-control';
import type { ControlSize } from '../control-styles';

export type TimeRangePreset = 'today' | '7d' | '30d' | '90d' | '12m' | 'custom';

export interface TimeRange {
  preset: TimeRangePreset;
  /** ISO date string (yyyy-mm-dd). Only meaningful when preset === 'custom'. */
  from?: string;
  /** ISO date string (yyyy-mm-dd). Only meaningful when preset === 'custom'. */
  to?: string;
}

export interface TimeRangePickerProps {
  size?: ControlSize;
  value: TimeRange;
  onChange: (next: TimeRange) => void;
  timezone: string;
  /** Hide some presets per surface. Default = show all. */
  presets?: ReadonlyArray<TimeRangePreset>;
  className?: string;
}

const PRESET_LABELS: Record<TimeRangePreset, string> = {
  today: 'Today',
  '7d': '7 days',
  '30d': '30 days',
  '90d': '90 days',
  '12m': '12 months',
  custom: 'Custom',
};

const DEFAULT_PRESETS: ReadonlyArray<TimeRangePreset> = [
  'today',
  '7d',
  '30d',
  '90d',
  '12m',
  'custom',
];

export function TimeRangePicker({
  size = 'toolbar',
  value,
  onChange,
  timezone,
  presets = DEFAULT_PRESETS,
  className,
}: TimeRangePickerProps) {
  // Keep incomplete Custom edits local while the parent retains the applied
  // range. Sync external changes by content so an equal recreated value does
  // not discard a partially entered draft.
  const [draft, setDraft] = React.useState(value);
  const customOpen = draft.preset === 'custom';
  React.useEffect(
    () => setDraft({ preset: value.preset, from: value.from, to: value.to }),
    [value.preset, value.from, value.to],
  );
  const rangeError = customOpen ? resolveTimeRangeToIso(draft, timezone).error : undefined;
  const errorId = React.useId();
  function changeCustom(next: TimeRange) {
    setDraft(next);
    if (next.from && next.to && next.from <= next.to) onChange(next);
  }

  function handlePreset(preset: TimeRangePreset) {
    if (preset === 'custom') {
      setDraft({ preset: 'custom', from: value.from, to: value.to });
      return;
    }
    setDraft({ preset });
    onChange({ preset });
  }

  return (
    <div
      className={cn('inline-flex flex-wrap items-center gap-1.5', className)}
      role="group"
      aria-label="Time range"
      data-time-range-picker
    >
      <SegmentedControl
        aria-label="Time range preset"
        size={size}
        options={presets.map((preset) => ({ value: preset, label: PRESET_LABELS[preset] }))}
        value={draft.preset}
        onChange={handlePreset}
      />

      {rangeError && (
        <p id={errorId} role="alert">
          {rangeError} The displayed data still uses the last applied range.
        </p>
      )}
      {customOpen && (
        <div className="inline-flex items-center gap-1 text-[11.5px]">
          <Calendar className="h-3 w-3 text-text-muted" aria-hidden="true" />
          <DateEntryInput
            size={size}
            value={draft.from ?? ''}
            onChange={(nextValue) =>
              changeCustom({ preset: 'custom', from: nextValue || undefined, to: draft.to })
            }
            aria-label="From date"
            aria-invalid={Boolean(rangeError)}
            aria-describedby={rangeError ? errorId : undefined}
          />
          <span className="text-text-muted">→</span>
          <DateEntryInput
            size={size}
            value={draft.to ?? ''}
            onChange={(nextValue) =>
              changeCustom({ preset: 'custom', from: draft.from, to: nextValue || undefined })
            }
            aria-label="To date"
            aria-invalid={Boolean(rangeError)}
            aria-describedby={rangeError ? errorId : undefined}
          />
        </div>
      )}
    </div>
  );
}

/** Resolve inclusive tenant-local calendar days to UTC instants. Invalid custom
 * drafts return validation copy rather than throwing in dashboard loader effects. */
export function resolveTimeRangeToIso(
  range: TimeRange,
  timezone: string,
): { fromIso?: string; toIso?: string; error?: string } {
  if (!timezone) return { error: 'Choose a timezone before applying a range.' };
  if (range.preset === 'custom') {
    if (!range.from || !range.to) return { error: 'Choose both dates to apply Custom.' };
    if (!parseIsoDateInput(range.from) || !parseIsoDateInput(range.to))
      return { error: 'Choose valid dates.' };
    if (range.from > range.to) return { error: 'From date must be on or before To date.' };
  }
  try {
    const today = dateOnlyInTz(new Date(), timezone);
    const from =
      range.preset === 'custom'
        ? range.from!
        : range.preset === 'today'
          ? today
          : range.preset === '12m'
            ? addMonthsIso(today, -12)
            : addDaysIso(today, -(Number.parseInt(range.preset, 10) - 1));
    const to = range.preset === 'custom' ? range.to! : today;
    return {
      fromIso: dayBoundsInTz(from, timezone).startUtc,
      toIso: dayBoundsInTz(to, timezone).endUtc,
    };
  } catch {
    return { error: 'Unable to resolve these dates in the selected timezone.' };
  }
}
